import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Trophy, Star, TrendingUp, Award, Medal, ArrowLeft, User, Share2, Flame, Printer, Camera } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { printProfile } from "@/lib/printProfile";

const badgeIcon = { champion: Trophy, qualifier: Star, wins: Award, finish: Medal, regular: TrendingUp };

export default function PlayerProfile() {
  const { name } = useParams();
  const [profile, setProfile] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/players/${encodeURIComponent(name)}`).then((r) => setProfile(r.data)).catch(() => setNotFound(true));
  }, [name]);

  if (notFound) return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4">
      <p className="text-slate-400">Player not found.</p>
      <Link to="/leaderboards" className="text-gold underline">Back to Leaderboards</Link>
    </div>
  );
  if (!profile) return <div className="min-h-[60vh] flex items-center justify-center text-gold animate-pulse">Loading profile…</div>;

  const picture = profile.picture ? (profile.picture.startsWith("http") ? profile.picture : `${process.env.REACT_APP_BACKEND_URL}${profile.picture}`) : "";

  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) { await navigator.share({ title: `${profile.name} — Legends Poker`, url }); return; }
      await navigator.clipboard.writeText(url);
      toast.success("Profile link copied to clipboard!");
    } catch (e) {
      toast.error("Could not share link");
    }
  };

  const ord = (n) => n ? `${n}${{ 1: "st", 2: "nd", 3: "rd" }[n % 10 > 3 || (n % 100 >= 11 && n % 100 <= 13) ? 0 : n % 10] || "th"}` : "";
  const pb = profile.personal_best;

  return (
    <div data-testid="player-profile-page">
      <section className="relative border-b border-white/10 bg-navy-paper overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #D4AF37 0, transparent 45%)" }} />
        <div className="relative max-w-6xl mx-auto px-6 md:px-12 py-16 md:py-20">
          <Link to="/leaderboards" className="inline-flex items-center gap-2 text-slate-400 hover:text-gold mb-6 text-sm"><ArrowLeft className="w-4 h-4" /> Leaderboards</Link>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
            <div className="w-24 h-24 border-2 border-gold overflow-hidden bg-navy flex items-center justify-center flex-shrink-0" data-testid="profile-avatar">
              {picture ? <img src={picture} alt={profile.name} className="w-full h-full object-cover" /> : <User className="w-10 h-10 text-gold" />}
            </div>
            <div>
              <div className="text-gold text-xs uppercase tracking-[0.35em] mb-2">Legend</div>
              <h1 className="font-display text-4xl md:text-5xl font-bold text-white tracking-tight">{profile.name}</h1>
              <div className="flex flex-wrap gap-2 mt-4">
                {profile.badges.map((b, i) => {
                  const Icon = badgeIcon[b.type] || Star;
                  return (
                    <span key={b.type} className="inline-flex items-center gap-1.5 border border-gold/40 bg-gold/5 text-gold text-xs uppercase tracking-wider px-3 py-1.5" data-testid={`badge-${b.type}`}>
                      <Icon className="w-3.5 h-3.5" /> {b.label}
                    </span>
                  );
                })}
                {profile.badges.length === 0 && <span className="text-slate-500 text-sm">No badges yet — play an event to start earning them.</span>}
              </div>
            </div>
            <button onClick={share} className="sm:ml-auto inline-flex items-center gap-2 border border-gold/40 text-gold hover:bg-gold hover:text-navy transition-colors px-4 py-2.5 text-xs uppercase tracking-widest" data-testid="share-profile-btn">
              <Share2 className="w-4 h-4" /> Share
            </button>
            <button onClick={() => printProfile(profile)} className="inline-flex items-center gap-2 border border-gold/40 text-gold hover:bg-gold hover:text-navy transition-colors px-4 py-2.5 text-xs uppercase tracking-widest" data-testid="print-profile-btn">
              <Printer className="w-4 h-4" /> Print / PDF
            </button>
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-6 md:px-12 py-14">
        {pb && (
          <div className="mb-10 bg-gradient-to-r from-gold/15 to-transparent border border-gold/30 p-6 flex items-center gap-5" data-testid="personal-best">
            <Flame className="w-10 h-10 text-gold flex-shrink-0" />
            <div>
              <div className="text-gold text-xs uppercase tracking-[0.3em] mb-1">Personal Best</div>
              <div className="font-display text-xl md:text-2xl font-bold text-white">
                {ord(pb.place)} place — {pb.event_title}
              </div>
              <div className="text-slate-400 text-sm mt-1">{pb.points} points · {pb.venue} · {pb.season}{pb.payout ? ` · $${pb.payout.toLocaleString()}` : ""}</div>
            </div>
          </div>
        )}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-14">
          {[
            { label: "Total Points", value: profile.total_points },
            { label: "Event Wins", value: profile.wins },
            { label: "Events Played", value: profile.events_played },
            { label: "Best Finish", value: profile.best_finish ? `#${profile.best_finish}` : "—" },
          ].map((s, i) => (
            <div key={s.label} className="bg-navy-paper border border-slate-800 p-6" data-testid={`profile-stat-${i}`}>
              <div className="font-display text-3xl font-bold text-gold">{s.value}</div>
              <div className="text-slate-400 text-xs uppercase tracking-widest mt-1">{s.label}</div>
            </div>
          ))}
        </div>

        {profile.season_stats?.length > 0 && (
          <div className="mb-14" data-testid="season-comparison">
            <h2 className="font-display text-xl font-bold text-white mb-4">Season by Season</h2>
            <div className="border border-white/10 overflow-x-auto">
              <table className="w-full text-left min-w-[560px]">
                <thead className="bg-secondary text-gold text-xs uppercase tracking-wider">
                  <tr>
                    <th className="px-5 py-3">Season</th><th className="px-5 py-3">Rank</th>
                    <th className="px-5 py-3 text-right">Points</th><th className="px-5 py-3 text-right">Played</th>
                    <th className="px-5 py-3 text-right">Wins</th><th className="px-5 py-3 text-right">Best</th>
                    <th className="px-5 py-3 text-center">Finals</th>
                  </tr>
                </thead>
                <tbody>
                  {profile.season_stats.map((s) => (
                    <tr key={s.season} className="border-t border-white/5 hover:bg-white/5" data-testid={`season-row-${s.season}`}>
                      <td className="px-5 py-3 text-white text-sm font-medium">{s.season}</td>
                      <td className="px-5 py-3 text-gold font-semibold">{s.rank ? `#${s.rank} / ${s.total_players}` : "—"}</td>
                      <td className="px-5 py-3 text-right text-slate-200">{s.points}</td>
                      <td className="px-5 py-3 text-right text-slate-400">{s.events_played}</td>
                      <td className="px-5 py-3 text-right text-slate-400">{s.wins}</td>
                      <td className="px-5 py-3 text-right text-slate-300">{s.best_finish ? `#${s.best_finish}` : "—"}</td>
                      <td className="px-5 py-3 text-center">{s.qualifier && <Star className="w-4 h-4 text-gold inline" title="Qualified" />}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {profile.photos?.length > 0 && (
          <div className="mb-14" data-testid="profile-photos">
            <h2 className="font-display text-xl font-bold text-white mb-4 flex items-center gap-2"><Camera className="w-5 h-5 text-gold" /> Photos</h2>
            <div className="columns-2 md:columns-3 gap-4 space-y-4">
              {profile.photos.map((g) => (
                <div key={g.id} className="relative group break-inside-avoid overflow-hidden border border-white/10" data-testid={`profile-photo-${g.id}`}>
                  <img src={g.image.startsWith("http") ? g.image : `${process.env.REACT_APP_BACKEND_URL}${g.image}`} alt={g.caption} className="w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  {(g.caption || g.album) && (
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-navy/95 to-transparent p-3">
                      {g.caption && <div className="text-white text-sm">{g.caption}</div>}
                      {g.album && <div className="text-gold text-[10px] uppercase tracking-widest">{g.album}</div>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <h2 className="font-display text-xl font-bold text-white mb-4">Results History</h2>
        <div className="border border-white/10 overflow-x-auto">
          <table className="w-full text-left min-w-[640px]" data-testid="profile-results">
            <thead className="bg-secondary text-gold text-xs uppercase tracking-wider">
              <tr>
                <th className="px-5 py-3">Place</th><th className="px-5 py-3">Event</th>
                <th className="px-5 py-3">Venue</th><th className="px-5 py-3">Season</th>
                <th className="px-5 py-3">Date</th><th className="px-5 py-3 text-right">Points</th><th className="px-5 py-3 text-right">Payout</th>
              </tr>
            </thead>
            <tbody>
              {profile.results.map((r) => (
                <tr key={r.id} className="border-t border-white/5 hover:bg-white/5">
                  <td className="px-5 py-3 font-display font-bold text-gold">{r.place}</td>
                  <td className="px-5 py-3 text-white text-sm">{r.event_title}</td>
                  <td className="px-5 py-3 text-slate-400 text-sm">{r.venue}</td>
                  <td className="px-5 py-3 text-slate-500 text-sm">{r.season}</td>
                  <td className="px-5 py-3 text-slate-500 text-sm">{r.date}</td>
                  <td className="px-5 py-3 text-right text-slate-200">{r.points}</td>
                  <td className="px-5 py-3 text-right text-slate-300">{r.payout ? `$${r.payout.toLocaleString()}` : "—"}</td>
                </tr>
              ))}
              {profile.results.length === 0 && <tr><td colSpan={7} className="px-5 py-14 text-center text-slate-500">No results recorded yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
